import { Button, Card, Muted, Stack, color, space, status } from '@biopeak/ui';
import { useEffect, useRef, useState } from 'react';
import {
  useExtractDocument,
  useMarkDocumentsViewed,
  useOpenDocument,
  usePatientDocuments,
  type PatientDocument,
} from '../lib/documents.ts';
import { DocumentAnalysis } from './DocumentAnalysis.tsx';
import { DocumentReview } from './DocumentReview.tsx';
import { DocumentSideBySide } from './DocumentSideBySide.tsx';
import { useGenerateDocumentAnalysis } from '../lib/documentAnalysis.ts';

/**
 * Exames enviados pelo paciente.
 *
 * Nesta etapa a tela é deliberadamente simples: lista e abre o original. A
 * revisão dos valores extraídos — o portão em que o médico confirma cada número
 * contra o trecho de origem — é a etapa seguinte, e entra aqui como uma seção
 * dentro de cada documento.
 *
 * Entregar a listagem antes da extração não é meio caminho: "os exames do
 * paciente estão todos em um lugar só, ao lado do resto" já é o valor principal
 * para quem atende, e chega sem nenhum risco de leitura automática errada.
 */
export function PatientDocuments({ patientId }: { patientId: string }) {
  const documentos = usePatientDocuments(patientId);
  const marcarVistos = useMarkDocumentsViewed(patientId);

  // Uma vez por montagem da aba, não a cada refetch — `marcarVistos` na
  // dependência reagiria à própria invalidação num laço (mesmo cuidado do
  // `useMarkChatRead` em PatientCommunication.tsx).
  const jaMarcou = useRef(false);
  useEffect(() => {
    if (jaMarcou.current || !documentos.data || documentos.data.length === 0) return;
    jaMarcou.current = true;
    marcarVistos.mutate();
  }, [documentos.data, marcarVistos]);

  if (documentos.isLoading) {
    return <Muted>Carregando…</Muted>;
  }

  if (documentos.isError) {
    return (
      <Muted style={{ color: status.red.fg }}>
        Não foi possível carregar os exames agora.
      </Muted>
    );
  }

  if (!documentos.data || documentos.data.length === 0) {
    return (
      <Card>
        <Stack gap={space.xs}>
          <span style={{ fontSize: '15px', fontWeight: 600 }}>
            Nenhum exame enviado
          </span>
          <Muted style={{ fontSize: '14px' }}>
            O paciente envia exames pelo aplicativo, em "Meus exames". Você verá
            aqui o arquivo original assim que ele enviar.
          </Muted>
        </Stack>
      </Card>
    );
  }

  return (
    <Stack gap={space.sm}>
      {documentos.data.map((doc) => (
        <DocumentoCard key={doc.id} documento={doc} />
      ))}
    </Stack>
  );
}

/**
 * Rótulo do estado da leitura automática.
 *
 * `extraido` diz "aguardando sua conferência" e não "pronto": o valor lido por
 * modelo não vale clinicamente antes do médico confirmar, e o rótulo é o
 * primeiro lugar onde essa regra aparece para ele.
 */
function estado(s: string): { texto: string; tom: 'neutro' | 'atencao' } {
  switch (s) {
    case 'enviado':
      return { texto: 'Aguardando leitura', tom: 'neutro' };
    case 'em_leitura':
      return { texto: 'Em leitura', tom: 'neutro' };
    case 'extraido':
      return { texto: 'Aguardando sua conferência', tom: 'atencao' };
    case 'sem_achado':
      return { texto: 'Nenhum valor reconhecido', tom: 'neutro' };
    case 'revisado':
      return { texto: 'Conferido', tom: 'neutro' };
    case 'falha':
      return { texto: 'Leitura automática falhou', tom: 'atencao' };
    default:
      return { texto: s, tom: 'neutro' };
  }
}

function DocumentoCard({ documento }: { documento: PatientDocument }) {
  const abrir = useOpenDocument();
  const extrair = useExtractDocument();
  const analisar = useGenerateDocumentAnalysis(documento.id);
  const e = estado(documento.status);
  const [conferindo, setConferindo] = useState(false);

  // `extraido` é o estado em que existe leitura automática esperando o médico.
  // Só nele a conferência lado a lado é a ação principal da tela.
  const temPendencia = documento.status === 'extraido';

  // A leitura automática se oferece para o que ainda não foi lido, para o que
  // falhou, e TAMBÉM para o que ficou preso em "em_leitura" — inclui esse
  // último de propósito, por um caso de campo real: a função morreu no meio do
  // caminho (matada pela plataforma por estourar tempo) sem chegar a nenhum dos
  // blocos que marcam `falha`, e o documento ficou travado sem nenhum botão
  // para tentar de novo. Havia timeout algum aqui hoje protegeria contra a
  // causa exata que já aconteceu, mas não protege contra toda causa futura de
  // morte no meio do caminho — por isso a interface, e não só o backend, guarda
  // uma saída.
  //
  // NÃO inclui `extraido` nem `revisado`: nesses dois a extração já terminou
  // de verdade e há valores pendentes (ou confirmados) para aquele documento —
  // reler duplicaria linhas em vez de destravar algo. `sem_achado` ENTRA de
  // propósito: a leitura rodou e não reconheceu nada, mas não há linha
  // nenhuma gravada pra "reler" duplicar — vale oferecer tentar de novo (ex.:
  // o catálogo pode ter crescido desde então, como já aconteceu num caso
  // real de hemograma).
  const podeExtrair =
    documento.status === 'enviado'
    || documento.status === 'falha'
    || documento.status === 'em_leitura'
    || documento.status === 'sem_achado';

  // Data da COLETA quando conhecida, não a do envio: um exame de três meses
  // atrás enviado hoje é um exame de três meses atrás, e mostrar a data do
  // upload faria o médico ler um resultado antigo como recente.
  const dataClinica = documento.recorded_at
    ? `Coletado em ${new Date(documento.recorded_at).toLocaleDateString('pt-BR')}`
    : `Enviado em ${new Date(documento.uploaded_at).toLocaleDateString('pt-BR')}`;

  return (
    <Card compact>
      <Stack gap={space.sm}>
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            gap: space.sm,
            flexWrap: 'wrap',
          }}
        >
          <Stack gap="2px">
            <span style={{ fontSize: '15px', fontWeight: 600 }}>
              {documento.original_filename ?? 'Exame'}
            </span>
            <Muted style={{ fontSize: '12px' }}>
              {documento.kind === 'laboratorio' ? 'Laboratório' : 'Imagem'}
              {documento.lab_name ? ` · ${documento.lab_name}` : ''} · {dataClinica}
            </Muted>
          </Stack>

          <span
            style={{
              fontSize: '12px',
              fontWeight: 600,
              padding: `2px ${space.sm}`,
              borderRadius: '980px',
              color: e.tom === 'atencao' ? status.yellow.fg : color.textSecondary,
              background: e.tom === 'atencao' ? status.yellow.bg : 'transparent',
              border: `1px solid ${
                e.tom === 'atencao' ? status.yellow.border : color.border
              }`,
              whiteSpace: 'nowrap',
            }}
          >
            {e.texto}
          </span>
        </div>

        <div style={{ display: 'flex', gap: space.sm, flexWrap: 'wrap' }}>
          {/* Ação principal quando há o que conferir. "Abrir exame" continua
              existindo ao lado, mas para a outra tarefa: imprimir, baixar,
              mandar o arquivo original para alguém. Conferir é aqui dentro. */}
          <Button
            variant={temPendencia ? 'primary' : 'secondary'}
            onClick={() => setConferindo(true)}
          >
            {temPendencia ? 'Conferir lado a lado' : 'Ver lado a lado'}
          </Button>

          <Button
            variant="secondary"
            onClick={() => abrir.mutate(documento)}
            disabled={abrir.isPending}
          >
            {abrir.isPending ? 'Abrindo…' : 'Abrir em nova aba'}
          </Button>

          {/* ANÁLISE DA IA, a terceira ação do exame.
              Fica ao lado das outras duas e não escondida numa aba porque a
              pergunta que ela responde ("o que este laudo diz junto?") só faz
              sentido ao lado do laudo.

              Oferecida em QUALQUER estado, inclusive antes da conferência: a
              função devolve "aguardando conferência" como resposta útil, e o
              médico descobre ali que o caminho é o botão ao lado. Esconder o
              botão até o exame estar conferido deixaria a feature invisível
              justamente para quem ainda não sabe que ela existe. */}
          <Button
            variant="secondary"
            onClick={() => analisar.mutate({ force: false })}
            disabled={analisar.isPending}
          >
            {analisar.isPending ? 'Analisando…' : 'Análise da IA'}
          </Button>

          {podeExtrair && (
            <Button
              variant="secondary"
              onClick={() => extrair.mutate(documento.id)}
              disabled={extrair.isPending}
            >
              {extrair.isPending ? 'Lendo…' : 'Ler automaticamente'}
            </Button>
          )}
        </div>

        {abrir.isError && (
          <Muted style={{ fontSize: '13px', color: status.red.fg }}>
            Não foi possível abrir o arquivo. Tente de novo.
          </Muted>
        )}

        {/* Foto e digitalização caem aqui: não é falha de sistema, é limite
            conhecido. Só texto sai daqui para o serviço de leitura, e não há
            como mascarar identificação numa imagem. */}
        {extrair.data?.status === 'sem_texto' && (
          <Muted style={{ fontSize: '13px' }}>{extrair.data.message}</Muted>
        )}

        {/* A leitura rodou (não é falha) mas não achou nenhum analito nem
            achado de imagem no texto — o arquivo pode não ser um exame, ou
            trazer só texto livre que o modelo não reconhece como dado
            estruturado. Sem esta explicação, "Nenhum valor reconhecido"
            sozinho não diz ao médico o que fazer a seguir. */}
        {documento.status === 'sem_achado' && (
          <Muted style={{ fontSize: '13px' }}>
            A leitura automática rodou, mas não encontrou nenhum valor de exame
            reconhecível neste arquivo. Abra o exame original para conferir o
            conteúdo, ou tente ler de novo.
          </Muted>
        )}

        {extrair.isError && (
          <Muted style={{ fontSize: '13px', color: status.red.fg }}>
            {extrair.error instanceof Error
              ? extrair.error.message
              : 'Não foi possível ler o documento.'}
          </Muted>
        )}

        <DocumentAnalysis
          documentId={documento.id}
          isPending={analisar.isPending}
          error={analisar.error}
          result={analisar.data}
          onRefazer={() => analisar.mutate({ force: true })}
        />

        <DocumentReview documentId={documento.id} />
      </Stack>

      {conferindo && (
        <DocumentSideBySide documento={documento} onClose={() => setConferindo(false)} />
      )}
    </Card>
  );
}
